import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { MenuService } from '../services/menu.service';
import { environment } from '../../../environments/environment';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    // Skip auth for mock data requests
    if (environment.useMockData || req.url.startsWith('/assets/mock-data')) {
        return next(req);
    }

    const authService = inject(AuthService);
    const menuService = inject(MenuService);
    const router = inject(Router);
    const isAuthLoginRequest = req.url.includes('/identity/auth/login');
    const isAuthRefreshRequest = req.url.includes('/identity/auth/refresh');

    let headers = req.headers;

    // Anti-cache headers for GET requests to prevent stale data
    if (req.method === 'GET') {
        headers = headers
            .set('Cache-Control', 'no-cache, no-store, must-revalidate')
            .set('Pragma', 'no-cache')
            .set('Expires', '0');
    }

    // Add environment ID header
    if (environment.environmentId) {
        headers = headers.set('X-Environment-ID', environment.environmentId);
    }

    // Add Bearer token if present and not already set
    const token = authService.getToken();
    if (token && !headers.has('Authorization') && !isAuthLoginRequest && !isAuthRefreshRequest) {
        headers = headers.set('Authorization', `Bearer ${token}`);
    }

    const isAuthenticatedRequest =
        headers.has('Authorization') && !isAuthLoginRequest && !isAuthRefreshRequest;

    if (isAuthenticatedRequest && isConfiguredApiOriginRequest(req.url)) {
        const currentPageId = menuService.getCurrentPageId();
        if (isValidPageId(currentPageId)) {
            headers = headers.set('X-Page-ID', String(currentPageId));
        }
    }

    const clonedReq = req.clone({ headers });

    return next(clonedReq).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status === 401 && !isAuthLoginRequest && !isAuthRefreshRequest) {
                return handle401Error(clonedReq, next, authService, router);
            }

            return throwError(() => error);
        })
    );
};

function handle401Error(
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
    authService: AuthService,
    router: Router
): Observable<HttpEvent<unknown>> {
    if (authService.startTokenRefresh()) {
        return authService.refreshAuthToken().pipe(
            switchMap((success) => {
                if (success) {
                    const newToken = authService.getToken();
                    authService.completeTokenRefresh(newToken);
                    const retryReq = request.clone({
                        headers: request.headers.set('Authorization', `Bearer ${newToken}`),
                    });
                    return next(retryReq);
                }

                authService.completeTokenRefresh(null);
                authService.logout();
                router.navigate(['/login']);
                return EMPTY;
            }),
            catchError(() => {
                authService.completeTokenRefresh(null);
                authService.logout();
                router.navigate(['/login']);
                return EMPTY;
            })
        );
    }

    // Another request is already refreshing → wait for the new token
    return authService.waitForTokenRefresh().pipe(
        switchMap((token) => {
            const retryReq = request.clone({
                headers: request.headers.set('Authorization', `Bearer ${token}`),
            });
            return next(retryReq);
        })
    );
}

function isConfiguredApiOriginRequest(url: string): boolean {
    const requestOrigin = resolveUrlOrigin(url);
    const apiOrigin = resolveUrlOrigin(environment.identityApiUrl);

    return !!requestOrigin && !!apiOrigin && requestOrigin === apiOrigin;
}

function resolveUrlOrigin(url: string): string | null {
    try {
        const baseUrl =
            typeof window !== 'undefined' && window.location?.origin
                ? window.location.origin
                : environment.identityApiUrl;

        return new URL(url, baseUrl).origin;
    } catch {
        return null;
    }
}

function isValidPageId(pageId: number | null): pageId is number {
    return typeof pageId === 'number' && Number.isInteger(pageId) && pageId > 0;
}
